import type {
  CreateWorkerPaymentMethodType,
  CreateStaffWorkerRequest,
  EmploymentTemplate,
} from "services/api/company/staff.service";
import {
  getStockholmLocalDateNow,
  toIsoStartOfDayUtcFromStockholmDate,
} from "web-apps/company/utils/timeAndDate";
import { normalizePaymentMethodValue } from "../shared/staff.paymentMethod.logic";
import type { AddStaffFormData } from "./addStaffPanel.contract";
import {
  toEmploymentTemplateSharedFields,
  toTemplateSalaryString,
} from "../shared/staffEmploymentTemplate.logic";
import {
  getEmploymentFieldVisibility,
  getVacationFieldVisibility,
} from "../shared/staffEmploymentFieldVisibility.logic";
import { DEFAULT_VACATION_TERMS } from "../shared/staffVacation.logic";

const DEFAULT_TAX_TABLE = 33;

/**
 * Creates default form state for the add staff panel.
 */
export const createInitialFormData = (): AddStaffFormData => {
  return {
    phoneNumber: "",
    employeeNumber: "",
    firstName: "",
    lastName: "",
    email: "",
    personalIdentityNumber: "",
    role: "",
    teamIds: [],
    employmentType: undefined,
    salaryType: undefined,
    salaryAmount: "",
    workPercentage: "100",
    taxTable: String(DEFAULT_TAX_TABLE),
    paymentMethodType: "BankAccount",
    paymentMethodValue: "",
    paymentType: "None",
    employeeCategory: "BlueCollar",
    startDate: getStockholmLocalDateNow(),
    endDate: null,
    agreement: "",
    noticeDays: "",
    vacationDays: "",
    workplace: "",
    ...DEFAULT_VACATION_TERMS,
  };
};

type ComparableAddStaffFormData = Omit<
  AddStaffFormData,
  "employeeNumber" | "endDate" | "startDate"
> & {
  endDate: string | null;
  startDate: string | null;
};

const toComparableAddStaffFormData = ({
  endDate,
  employeeNumber: _employeeNumber,
  startDate,
  ...formData
}: AddStaffFormData): ComparableAddStaffFormData => ({
  ...formData,
  endDate: endDate?.toString() ?? null,
  startDate: startDate?.toString() ?? null,
});

const stringifyComparableAddStaffFormData = (
  formData: AddStaffFormData,
): string => {
  return JSON.stringify(
    Object.entries(toComparableAddStaffFormData(formData)).sort(([a], [b]) =>
      a.localeCompare(b),
    ),
  );
};

export const isAddStaffFormStarted = (
  formData: AddStaffFormData,
  initialFormData: AddStaffFormData,
): boolean => {
  return (
    stringifyComparableAddStaffFormData(formData) !==
    stringifyComparableAddStaffFormData(initialFormData)
  );
};

const toFiniteNumberOrFallback = (
  value: string | undefined,
  fallback: number,
): number => {
  if (!value || value.trim().length === 0) return fallback;
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const toWorkPercentageFractionOrZero = (value?: string): number => {
  const percentValue = toFiniteNumberOrFallback(value, 0);
  if (percentValue <= 0) return 0;
  return Math.min(percentValue / 100, 1);
};

const toNonNegativeIntegerOrZero = (value?: string | number): number => {
  if (typeof value === "number") {
    if (!Number.isSafeInteger(value)) return 0;
    return value >= 0 ? value : 0;
  }
  if (!value || value.trim().length === 0) return 0;
  const trimmedValue = value.trim();
  if (!/^\d+$/.test(trimmedValue)) return 0;
  const parsedValue = Number(trimmedValue);
  if (!Number.isSafeInteger(parsedValue)) return 0;
  return parsedValue;
};

const toCreateWorkerPaymentMethodType = (
  value: AddStaffFormData["paymentMethodType"],
): CreateWorkerPaymentMethodType => {
  if (value === "InternationalBankAccount") {
    return "InternationalBankAccount";
  }

  return "bankAccount";
};

/**
 * Applies a selected employment template onto the add staff form, leaving
 * personal details and the tax table untouched.
 */
export const applyEmploymentTemplateToAddStaffForm = (
  formData: AddStaffFormData,
  template: EmploymentTemplate,
): AddStaffFormData => {
  return {
    ...formData,
    ...toEmploymentTemplateSharedFields(template, formData),
    salaryAmount: toTemplateSalaryString(
      template.salaryValue,
      formData.salaryAmount,
    ),
  };
};

/**
 * Maps add staff form values to API payload for worker creation. The phone
 * number arrives already in the form the API stores, because converting it is
 * the one step that can fail and the caller answers for that.
 */
export const mapAddStaffFormDataToWorkerPayload = (
  formData: AddStaffFormData,
  apiPhoneNumber: string,
): CreateStaffWorkerRequest => {
  const employmentType = formData.employmentType ?? "Undefined";

  // Fields hidden for the selected employment type are never sent with stale
  // user input: forced fields fall back to their forced value, the rest to a
  // neutral default.
  const visibility = getEmploymentFieldVisibility(formData.employmentType);
  // Salary model is required whenever it is visible (submit is blocked until
  // set), so the visible "Undefined" fallback is unreachable; when hidden it is
  // forced to Hourly.
  const salaryType = visibility.showSalaryType
    ? formData.salaryType || "Undefined"
    : "Hourly";
  const employeeCategory = visibility.showEmployeeCategory
    ? formData.employeeCategory.trim() || "BlueCollar"
    : "BlueCollar";
  const paymentType = visibility.showPaymentType
    ? formData.paymentType || "None"
    : "None";
  const salaryValue = visibility.showSalary
    ? toFiniteNumberOrFallback(formData.salaryAmount, 0)
    : 0;
  const taxValue = visibility.showTaxTable
    ? toNonNegativeIntegerOrZero(formData.taxTable)
    : DEFAULT_TAX_TABLE;
  const agreement = visibility.showContractTerms
    ? formData.agreement.trim()
    : "";
  const vacationDays = visibility.showContractTerms
    ? toNonNegativeIntegerOrZero(formData.vacationDays)
    : 0;
  const noticeDays = visibility.showContractTerms
    ? toNonNegativeIntegerOrZero(formData.noticeDays)
    : 0;
  const workplace = visibility.showContractTerms
    ? formData.workplace.trim()
    : "";
  const workPercentage =
    salaryType === "Monthly"
      ? toWorkPercentageFractionOrZero(formData.workPercentage)
      : 0;
  const startDate =
    (formData.startDate != null
      ? toIsoStartOfDayUtcFromStockholmDate(formData.startDate)
      : null) ??
    toIsoStartOfDayUtcFromStockholmDate(getStockholmLocalDateNow());
  const endDate =
    formData.endDate != null
      ? toIsoStartOfDayUtcFromStockholmDate(formData.endDate)
      : null;
  const paymentMethodValue = normalizePaymentMethodValue(
    formData.paymentMethodValue,
  );
  const vacationVisibility = getVacationFieldVisibility(formData.salaryType);
  const vacationPayoutType = vacationVisibility.showVacationPayoutType
    ? formData.vacationPayoutType
    : "Undefined";
  const toVacationEarningDays = (value: string): number =>
    vacationVisibility.showVacationEarningDays
      ? toNonNegativeIntegerOrZero(value)
      : 0;

  const payload: CreateStaffWorkerRequest = {
    worker: {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      phoneNumber: apiPhoneNumber,
      email: formData.email.trim(),
      personalIdentityNumber: formData.personalIdentityNumber.trim(),
    },
    employment: {
      startDate,
      endDate,
      employmentType,
      employeeNumber: toNonNegativeIntegerOrZero(formData.employeeNumber),
      employeeCategory,
      salaryType,
      workPercentage,
      paymentType,
      role: formData.role.trim(),
      agreement,
      vacationDays,
      noticeDays,
      workplace,
      vacationPayoutType,
      maxSickLeaveDays: toVacationEarningDays(formData.maxSickLeaveDays),
      maxCareOfChildDays: toVacationEarningDays(formData.maxCareOfChildDays),
      maxParentalLeaveDays: toVacationEarningDays(
        formData.maxParentalLeaveDays,
      ),
      advanceVacationDays: toVacationEarningDays(formData.advanceVacationDays),
      probationPeriodEndDate: null,
      salaryValue,
      taxType: "Undefined",
      taxValue,
      ...(paymentMethodValue.length > 0
        ? {
            paymentMethodType: toCreateWorkerPaymentMethodType(
              formData.paymentMethodType,
            ),
            paymentMethodValue,
          }
        : {}),
    },
    workGroupIds: formData.teamIds
      .map((id) => Number(id))
      .filter((id) => Number.isSafeInteger(id) && id >= 0),
  };

  return payload;
};
